import { httpsCallable } from "firebase/functions";
import { functions } from "@/firebase";
import { isTrustedMembershipFeatureEnabled } from "@/config/runtimeConfig";

const OPERATION_ID_PATTERN = /^[A-Za-z0-9_-]{16,128}$/;
const TARGET_ID_PATTERN = /^[A-Za-z0-9._:@-]{1,128}$/;
const OUTCOMES = new Set(["created", "updated", "replayed", "unchanged"]);
const SAFE_ERROR_MESSAGES = Object.freeze({
  "feature-disabled": "Trusted membership is not enabled.",
  "invalid-argument": "The membership request is invalid.",
  "invalid-response": "The membership service returned an invalid response.",
  unauthenticated: "Sign in before updating organisation membership.",
  "permission-denied": "This membership action is not available.",
  "failed-precondition": "The membership could not be reconciled.",
  aborted: "The membership operation conflicted with another request.",
  unavailable: "The membership service is temporarily unavailable.",
  "deadline-exceeded": "The membership service did not respond in time.",
  unknown: "The membership operation could not be completed.",
});

export class TrustedMembershipServiceError extends Error {
  constructor(code = "unknown") {
    const safeCode = Object.prototype.hasOwnProperty.call(
      SAFE_ERROR_MESSAGES,
      code,
    )
      ? code
      : "unknown";
    super(SAFE_ERROR_MESSAGES[safeCode]);
    this.name = "TrustedMembershipServiceError";
    this.code = safeCode;
  }
}

function fail(code) {
  throw new TrustedMembershipServiceError(code);
}

function isRecord(value) {
  return value !== null && typeof value === "object" && !Array.isArray(value);
}

function assertExactKeys(value, expectedKeys) {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join(",") !== [...expectedKeys].sort().join(",")
  ) {
    fail("invalid-argument");
  }
}

function validateOperationId(value) {
  if (typeof value !== "string" || !OPERATION_ID_PATTERN.test(value)) {
    fail("invalid-argument");
  }
  return value;
}

function validateOrgId(value) {
  const orgId = typeof value === "string" ? value.trim() : "";
  const reservedFirestoreId =
    orgId === "." || orgId === ".." || /^__.*__$/.test(orgId);
  if (!TARGET_ID_PATTERN.test(orgId) || reservedFirestoreId) {
    fail("invalid-argument");
  }
  return orgId;
}

function validateInput(value) {
  assertExactKeys(value, ["operationId", "orgId"]);
  return Object.freeze({
    operationId: validateOperationId(value.operationId),
    orgId: validateOrgId(value.orgId),
  });
}

function validateResponse(value) {
  if (
    !isRecord(value) ||
    Object.keys(value).sort().join(",") !== "active,outcome" ||
    typeof value.active !== "boolean" ||
    !OUTCOMES.has(value.outcome)
  ) {
    fail("invalid-response");
  }
  return Object.freeze({
    active: value.active,
    outcome: value.outcome,
  });
}

function normalizeRemoteError(error) {
  if (error instanceof TrustedMembershipServiceError) return error;
  const rawCode = typeof error?.code === "string" ? error.code : "";
  const code = rawCode.includes("/")
    ? rawCode.slice(rawCode.lastIndexOf("/") + 1)
    : rawCode;
  return new TrustedMembershipServiceError(code || "unknown");
}

function defaultCryptoProvider() {
  return typeof globalThis !== "undefined" ? globalThis.crypto : undefined;
}

export function createTrustedMembershipOperationId(
  cryptoProvider = defaultCryptoProvider(),
) {
  let operationId = "";
  if (typeof cryptoProvider?.randomUUID === "function") {
    operationId = cryptoProvider.randomUUID();
  } else if (typeof cryptoProvider?.getRandomValues === "function") {
    const bytes = cryptoProvider.getRandomValues(new Uint8Array(24));
    operationId = Array.from(bytes, (byte) =>
      byte.toString(16).padStart(2, "0"),
    ).join("");
  }
  return validateOperationId(operationId);
}

export function createTrustedMembershipService({
  functionsInstance = functions,
  callableFactory = httpsCallable,
  enabled = isTrustedMembershipFeatureEnabled,
} = {}) {
  let reconcileCallable = null;

  return Object.freeze({
    async reconcileOwnTrustedMembership(input) {
      if (enabled !== true) fail("feature-disabled");
      const payload = validateInput(input);
      if (!reconcileCallable) {
        reconcileCallable = callableFactory(
          functionsInstance,
          "reconcileOwnTrustedMembership",
        );
      }

      try {
        const response = await reconcileCallable(payload);
        return validateResponse(response?.data);
      } catch (error) {
        throw normalizeRemoteError(error);
      }
    },
  });
}

export const trustedMembershipService = createTrustedMembershipService();

export async function reconcileOwnTrustedMembershipAfterMutation(
  orgId,
  {
    enabled = isTrustedMembershipFeatureEnabled,
    operationIdFactory = createTrustedMembershipOperationId,
    service = trustedMembershipService,
  } = {},
) {
  if (enabled !== true) return null;
  const operationId = operationIdFactory();
  return service.reconcileOwnTrustedMembership({ operationId, orgId });
}
